import React, { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import { useNavigate } from "react-router-dom";
import { AlertError, AlertSuccess, AlertWarning } from "./components/Alert";
import Button from "./components/Button";
import Loading from "./components/Loading";

type Phase = "checking" | "ready" | "invalid";

function ResetPassword() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>("checking");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // ลิงก์เสีย/หมดอายุ — Supabase ส่ง error กลับมาใน query string
    const params = new URLSearchParams(window.location.search);
    if (params.get("error") || params.get("error_description")) {
      setPhase("invalid");
      return;
    }

    // เคสเปิดลิงก์ซ้ำหลัง exchange แล้ว (มี session ค้าง)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setPhase("ready");
    });

    // ปกติ: ลิงก์จากอีเมลมาพร้อม code → supabase-js (PKCE) exchange แล้วยิง event
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") && session) {
        setPhase("ready");
      }
    });

    // 8 วินาทียังไม่ได้ session = ลิงก์ไม่ใช้ได้
    const timer = setTimeout(() => setPhase((p) => (p === "checking" ? "invalid" : p)), 8000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timer);
    };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (newPassword.length < 6 || newPassword !== confirmPassword) {
      AlertWarning("รหัสผ่านต้องยาว 6 ตัวอักษรขึ้นไป และตรงกันทั้งสองช่อง");
      return;
    }
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      const msg = error.message.toLowerCase();
      if (msg.includes("different")) {
        AlertError("รหัสผ่านใหม่ต้องไม่ซ้ำกับรหัสผ่านเดิม");
      } else if (msg.includes("at least 6")) {
        AlertError("รหัสผ่านต้องยาว 6 ตัวอักษรขึ้นไป");
      } else {
        AlertError(error.message);
      }
      setSaving(false);
      return;
    }
    AlertSuccess("ตั้งรหัสผ่านใหม่สำเร็จ กรุณาเข้าสู่ระบบอีกครั้ง");
    await supabase.auth.signOut();
    navigate("/login", { replace: true });
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <Loading isLoading={saving} />
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-8 p-6 sm:p-8">
          <div className="flex justify-center mb-4">
            <img src="./images/lotus.jpg" alt="StockCars" className="max-h-[120px] object-contain" />
          </div>
          <h5 className="text-center text-lg font-semibold text-gray-900 mb-4">ตั้งรหัสผ่านใหม่</h5>

          {phase === "checking" && (
            <p className="text-center text-sm text-gray-500">กำลังตรวจสอบลิงก์...</p>
          )}

          {phase === "invalid" && (
            <div className="text-center">
              <p className="text-sm text-gray-700 mb-5">ลิงก์ไม่ถูกต้อง หมดอายุ หรือถูกใช้ไปแล้ว</p>
              <p className="text-sm text-gray-500 mb-5">กรุณาขอลิงก์ใหม่อีกครั้งจากหน้าเข้าสู่ระบบ (กด "ลืมรหัสผ่าน?") โดยใช้เบราว์เซอร์เดียวกับที่จะเปิดอีเมล</p>
              <Button variant="primary" size="full" onClick={() => navigate("/login")}>
                กลับไปหน้าเข้าสู่ระบบ
              </Button>
            </div>
          )}

          {phase === "ready" && (
            <form onSubmit={handleSubmit}>
              <input
                type="password"
                placeholder="รหัสผ่านใหม่ (6 ตัวอักษรขึ้นไป)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <input
                type="password"
                placeholder="ยืนยันรหัสผ่านใหม่"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <Button type="submit" variant="primary" size="full">
                บันทึกรหัสผ่านใหม่
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
